import { useMemo, useCallback } from "react";
import { Control, FieldValues } from "react-hook-form";
import { FormAsyncSelect } from "@covertree/ui";
import { theme } from "@covertree/theme";
import { useLazyGetManufacturersQuery } from "@redux/coverages/coveragesApi";

type Props<TValue extends FieldValues> = {
  control: Control<TValue>;
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultValue?: any;
  optional?: boolean;
  disabled?: boolean;
};

export function CoverageManufacturerSelect<TValue extends FieldValues>({
  control,
  name,
  defaultValue = "",
  optional = false,
  disabled = false,
}: Props<TValue>) {
  const [getManufacturers, { data, isFetching }] =
    useLazyGetManufacturersQuery();

  const manufacturersData = useMemo(() => {
    if (!data) return [];
    if (!data.includes("Not Listed")) {
      return [...data, "Not Listed"];
    }
    return data;
  }, [data]);

  const handleGetAsyncData = useCallback(
    async ({ variables }: { variables: { search: string } }) => {
      await getManufacturers({ search: variables.search });
    },
    [getManufacturers]
  );

  return (
    <FormAsyncSelect
      id="manufacturer-field"
      dataTestId="manufacturer-input"
      name={name}
      label="Manufacturer"
      placeholder="Type at least 3 characters"
      data={manufacturersData}
      initialData={manufacturersData}
      loading={isFetching}
      control={control}
      defaultSearch="America"
      defaultValue={defaultValue}
      inputStyle={{
        padding: "0px 16px 0px 0px !important",
        backgroundColor: theme.palette.background.default,
      }}
      inputProps={{
        sx: {
          padding: "0 13px 0 14px !important",
        },
      }}
      onGetAsyncData={handleGetAsyncData}
      optional={optional}
      disabled={disabled}
    />
  );
}
